import React, { createContext, useContext, useReducer } from 'react';
import { ReactEditor } from 'slate-react';
import { Editor, Node, Text, Transforms } from 'slate';
import { Range } from 'slate/dist/interfaces/range';
import { HistoryEditor } from 'slate-history';
interface EditorChange {
  editor: ReactEditor;
  timestamp: number;
  undone: boolean;
}

interface EditorState {
  editors: Record<string, ReactEditor | null>;
  editorChanges: EditorChange[];
  currentChangeIndex: number;
}

type EditorAction =
  | { type: 'ADD_EDITOR'; payload: { id: string; editor: ReactEditor } }
  | { type: 'REMOVE_EDITOR'; payload: { id: string } }
  | { type: 'ADD_EDITOR_CHANGE'; payload: { editor: ReactEditor } }
  | { type: 'MARK_CHANGE_UNDONE'; payload: { index: number } }
  | { type: 'MARK_CHANGE_REDONE'; payload: { index: number } }
  | { type: 'UPDATE_CURRENT_INDEX'; payload: { index: number } };

// Limit editor changes to prevent memory leaks (see docs/MEMORY_OPTIMIZATION.md)
// Each change stores a ReactEditor reference which can be large
const MAX_EDITOR_CHANGES = 50;

const initialState: EditorState = {
  editors: {},
  editorChanges: [],
  currentChangeIndex: -1
};

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'ADD_EDITOR':
      return {
        ...state,
        editors: {
          ...state.editors,
          [action.payload.id]: action.payload.editor
        }
      };
    case 'REMOVE_EDITOR':
      // eslint-disable-next-line no-case-declarations
      const updatedEditors = { ...state.editors };
      delete updatedEditors[action.payload.id];
      return { ...state, editors: updatedEditors };
    case 'ADD_EDITOR_CHANGE':
      // eslint-disable-next-line no-case-declarations
      const TIME_THRESHOLD = 2000; // 2 seconds

      // Add new change if:
      // - No changes exist yet
      // - Or it's a different editor
      // - Or enough time has passed since last change
      if (
        state.editorChanges.length === 0 ||
        state.editorChanges[0].editor !== action.payload.editor ||
        Date.now() - state.editorChanges[0].timestamp > TIME_THRESHOLD
      ) {
        const newChanges = [
          {
            editor: action.payload.editor,
            timestamp: Date.now(),
            undone: false
          },
          ...state.editorChanges
        ];

        // Trim to MAX_EDITOR_CHANGES to prevent unbounded growth
        const trimmedChanges =
          newChanges.length > MAX_EDITOR_CHANGES
            ? newChanges.slice(0, MAX_EDITOR_CHANGES)
            : newChanges;

        return {
          ...state,
          editorChanges: trimmedChanges,
          currentChangeIndex: state.currentChangeIndex + 1
        };
      }
      return state;
    case 'MARK_CHANGE_UNDONE':
      return {
        ...state,
        editorChanges: state.editorChanges.map((change, index) =>
          index === action.payload.index ? { ...change, undone: true } : change
        )
      };
    case 'MARK_CHANGE_REDONE':
      return {
        ...state,
        editorChanges: state.editorChanges.map((change, index) =>
          index === action.payload.index ? { ...change, undone: false } : change
        )
      };
    case 'UPDATE_CURRENT_INDEX':
      return {
        ...state,
        currentChangeIndex: action.payload.index
      };
    default:
      return state;
  }
}

const SharedCaptionEditorContext = createContext<{
  editors: Record<string, ReactEditor | null>;
  addEditor: (id: string, editor: ReactEditor) => void;
  removeEditor: (id: string) => void;
  undo: () => void;
  redo: () => void;
  trackModification: (editor: ReactEditor) => void;
  replaceAllWordsInEditor: (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => void;
}>({
  editors: {},
  addEditor: () => {},
  removeEditor: () => {},
  replaceAllWordsInEditor: () => {},
  undo: () => {},
  redo: () => {},
  trackModification: () => {}
});

export const SharedCaptionEditorProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(editorReducer, initialState);

  const addEditor = (id: string, editor: ReactEditor) => {
    dispatch({
      type: 'ADD_EDITOR',
      payload: { id: `editor-${id}`, editor }
    });
  };

  const removeEditor = (id: string) => {
    dispatch({ type: 'REMOVE_EDITOR', payload: { id: `editor-${id}` } });
  };

  const replaceAllWordsInEditor = (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => {
    for (const editor of Object.values(state.editors)) {
      if (editor) {
        handleReplaceAll(
          editor,
          searchText,
          caseSensitive,
          wholeWord,
          replacement
        );
      }
    }
  };

  const findMatches = (
    editor: Editor,
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean
  ) => {
    if (!searchText) return [];

    const matches: Range[] = [];
    const flags = caseSensitive ? 'g' : 'gi';
    const searchQuery = wholeWord ? `\\b${searchText}\\b` : searchText;
    const regex = new RegExp(searchQuery, flags);

    // Traverse through all text nodes and find matches
    for (const [node, path] of Node.texts(editor)) {
      if (Text.isText(node)) {
        const text = node.text;
        let match;

        // Search for matches in the text node
        while ((match = regex.exec(text)) !== null) {
          const start = match.index;
          const end = start + match[0].length;

          // Construct the valid range for Slate
          const range = {
            anchor: { path, offset: start },
            focus: { path, offset: end }
          };

          matches.push(range);
        }
      }
    }

    return matches;
  };

  const handleReplaceAll = (
    editor: Editor,
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => {
    const matches = findMatches(editor, searchText, caseSensitive, wholeWord);
    if (matches.length === 0) return;

    // Reverse iteration prevents path invalidation when replacing text
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      Transforms.select(editor, match);
      Transforms.insertText(editor, replacement || '');
    }
  };

  const trackModification = (editor: ReactEditor) => {
    dispatch({
      type: 'ADD_EDITOR_CHANGE',
      payload: { editor }
    });
  };

  const handleUndo = () => {
    const changes = state.editorChanges;
    if (changes.length === 0) return;

    // Start from the beginning (most recent change) and go forward
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      if (!change.undone) {
        const editor = change.editor;
        if (HistoryEditor.isHistoryEditor(editor as unknown as HistoryEditor)) {
          ReactEditor.focus(editor);
          HistoryEditor.undo(editor as unknown as HistoryEditor);
          dispatch({ type: 'MARK_CHANGE_UNDONE', payload: { index: i } });
          dispatch({
            type: 'UPDATE_CURRENT_INDEX',
            payload: { index: i }
          });
        }
        break;
      }
    }
  };

  const handleRedo = () => {
    const changes = state.editorChanges;
    if (changes.length === 0) return;

    // Start from the beginning (most recent change) and go forward
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      if (change.undone) {
        const editor = change.editor;
        if (HistoryEditor.isHistoryEditor(editor as unknown as HistoryEditor)) {
          ReactEditor.focus(editor);
          HistoryEditor.redo(editor as unknown as HistoryEditor);
          dispatch({ type: 'MARK_CHANGE_REDONE', payload: { index: i } });
          dispatch({ type: 'UPDATE_CURRENT_INDEX', payload: { index: i } });
        }
        break;
      }
    }
  };

  return (
    <SharedCaptionEditorContext.Provider
      value={{
        editors: state.editors,
        addEditor,
        removeEditor,
        replaceAllWordsInEditor,
        undo: handleUndo,
        redo: handleRedo,
        trackModification
      }}
    >
      {children}
    </SharedCaptionEditorContext.Provider>
  );
};

export const useSharedCaptionEditorContext = () =>
  useContext(SharedCaptionEditorContext);
